import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { formatDateOnly } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { PrintedDocument } from "@/components/app/documents/printed-document";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Printer } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { PrintedCarePass } from "./-components/printed-care-pass";

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute("/_authenticated/_care/care-pass/print")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { residentId: string; from: string; to: string } => ({
    residentId: typeof search.residentId === "string" ? search.residentId : "",
    from: typeof search.from === "string" && DAY.test(search.from) ? search.from : "",
    to: typeof search.to === "string" && DAY.test(search.to) ? search.to : "",
  }),
  component: PrintCarePass,
});

/** One resident's care pass record on paper, opened from their chart. The print dialog comes up on
 * its own once the sheets have loaded; the button is for another copy. */
function PrintCarePass() {
  const { residentId, from, to } = Route.useSearch();
  const complete = Boolean(residentId && from && to);
  const history = useQuery({
    ...orpc.care.carePass.forResident.queryOptions({ input: { residentId, from, to } }),
    enabled: complete,
  });
  const printed = useRef(false);
  useEffect(() => {
    if (!history.data || printed.current) return;
    printed.current = true;
    const timer = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(timer);
  }, [history.data]);

  return (
    <>
      <div className="mb-4 print:hidden">
        <PageHeader
          title="Print the care pass record"
          meta={
            history.data
              ? `${history.data.resident.firstName} ${history.data.resident.lastName}, ${formatDateOnly(from)} to ${formatDateOnly(to)}`
              : "Loading…"
          }
          actions={
            <Button disabled={!history.data} onClick={() => window.print()}>
              <Printer className="size-4" />
              Print
            </Button>
          }
        />
      </div>
      {!complete ? (
        <p className="text-muted-foreground text-sm">
          Open this page from a resident's Care pass tab, which says whose record and which days.
        </p>
      ) : history.isError ? (
        <ReadFailed
          message="Couldn't load the care pass record."
          error={history.error}
          retrying={history.isFetching}
          onRetry={() => void history.refetch()}
        />
      ) : history.data ? (
        <PrintedDocument orientation="landscape">
          <PrintedCarePass history={history.data} />
        </PrintedDocument>
      ) : (
        <Skeleton className="h-96 w-full" />
      )}
    </>
  );
}
